export type PdfPaper = "letter" | "a4";

export interface PdfItem {
  text: string;
  note?: string;
  checked: boolean;
  /** Rendered only when options.includeAttribution && checked. e.g. "Jane · Mar 3" */
  attribution?: string;
}

/** name === null means "ungrouped items directly under the section". */
export interface PdfGroup {
  name: string | null;
  items: PdfItem[];
}

export interface PdfSection {
  name: string;
  groups: PdfGroup[];
}

export interface PdfDocModel {
  /** Big display line. Empty → "Untitled". */
  title: string;
  /** Small letter-spaced line above the title, e.g. "PACKING LIST". */
  eyebrow: string;
  /** Joined with " · " under the title, e.g. ["32 items", "6 sections"]. */
  meta: string[];
  sections: PdfSection[];
  /** Left footer text before the date, e.g. "neattick.com". */
  footerLeft: string;
  generatedAt: Date;
  /** IANA zone used for generatedAt and every checkedAt on this document (T13). */
  displayZone: string;
  /** When set, a QR code linking here is drawn top-right on the masthead (page 1 only). */
  qrUrl?: string;
}

export interface PdfFontBytes {
  regular: Uint8Array | null;
  semibold: Uint8Array | null;
}

export interface PdfRenderOptions {
  paper: PdfPaper;
  /** false → every box blank (print & check off by hand). */
  showProgress: boolean;
  includeNotes: boolean;
  includeAttribution: boolean;
  colorAccents: boolean;
  fonts: PdfFontBytes;
}

/** Everything the dialog persists; `fonts` is supplied at render time. */
export type PdfUserOptions = Omit<PdfRenderOptions, "fonts">;

export const DEFAULT_PDF_OPTIONS: PdfUserOptions = {
  paper: "letter",
  showProgress: false,
  includeNotes: true,
  includeAttribution: false,
  colorAccents: true,
};

export const PDF_OPTIONS_STORAGE_KEY = "checklist:pdf-options:v1";
